import { assertNever } from "@towni/common";
import type React from "react";
import {
    forwardRef,
    type ForwardedRef,
    type HTMLAttributes,
    type ReactNode,
} from "react";
import { cn } from "../utils/cn";

const mainAxisClassMap: Record<NonNullable<ColumnProps["mainAxis"]>, string> = {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
    around: "justify-around",
    evenly: "justify-evenly",
};

const crossAxisClassMap: Record<
    NonNullable<ColumnProps["crossAxis"]>,
    string
> = {
    stretch: "items-stretch",
    center: "items-center",
    start: "items-start",
    end: "items-end",
    baseline: "items-baseline",
};

// Only allow rendering as block-level
// elements that are logical flex containers
type ColumnAs =
    | "div"
    | "section"
    | "article"
    | "aside"
    | "main"
    | "nav"
    | "header"
    | "footer";

type ColumnElementMap = {
    div: HTMLDivElement;
    section: HTMLElement;
    article: HTMLElement;
    aside: HTMLElement;
    main: HTMLElement;
    nav: HTMLElement;
    header: HTMLElement;
    footer: HTMLElement;
};

/**
 * Props for the `Column` component, allowing flexible layout configuration.
 *
 * @template T - The HTML element type to render as, defaults to `"div"`.
 * @property {ReactNode} [children] - The content to be rendered inside the column.
 * @property {string} [className] - Additional CSS class names to apply to the column.
 * @property {"start" | "center" | "end" | "between" | "around" | "evenly"} [mainAxis] -
 *   Controls alignment along the main axis (vertical by default). Defaults to `"start"`.
 * @property {"start" | "center" | "end" | "stretch" | "baseline"} [crossAxis] -
 *   Controls alignment along the cross axis (horizontal by default). Defaults to `"stretch"`.
 * @property {T} [as] - The HTML element to render as. Defaults to `"div"`.
 * @property {HTMLAttributes<ColumnElementMap[T]>} [props] - Additional HTML attributes for the rendered element.
 */
type ColumnProps<T extends ColumnAs = "div"> = {
    children?: ReactNode;
    className?: string;
    /** defaults to: `start` like the default of regular html/css */
    mainAxis?: "start" | "center" | "end" | "between" | "around" | "evenly";
    /** defaults to: `stretch` like the default of regular html/css */
    crossAxis?: "start" | "center" | "end" | "stretch" | "baseline";
    /** defaults to: `div` */
    as?: T;
} & HTMLAttributes<ColumnElementMap[T]>;

const ColumnComponent = <T extends ColumnAs>(
    props: ColumnProps<T>,
    ref: ForwardedRef<ColumnElementMap[T]> | null,
): React.ReactElement => {
    const {
        children,
        className = "",
        mainAxis = "start",
        crossAxis = "stretch",
        as: renderAs = "div",
        ...rest
    } = props;

    const flexClasses = cn(
        "flex flex-col",
        mainAxisClassMap[mainAxis],
        crossAxisClassMap[crossAxis],
        className,
    );

    // Use dynamic component rendering with proper typing for refs
    if (renderAs === "div") {
        return (
            <div
                ref={ref as ForwardedRef<HTMLDivElement>}
                className={flexClasses}
                {...rest}>
                {children}
            </div>
        );
    }
    switch (renderAs) {
        case "section":
            return (
                <section
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </section>
            );
        case "article":
            return (
                <article
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </article>
            );
        case "aside":
            return (
                <aside
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </aside>
            );
        case "main":
            return (
                <main
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </main>
            );
        case "nav":
            return (
                <nav
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </nav>
            );
        case "header":
            return (
                <header
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </header>
            );
        case "footer":
            return (
                <footer
                    ref={ref as ForwardedRef<HTMLElement>}
                    className={flexClasses}
                    {...rest}>
                    {children}
                </footer>
            );
        case "div":
        default:
            assertNever(renderAs);
    }
};

/**
 * A polymorphic React component that renders a column element.
 *
 * @typeParam T - The HTML element type to render as, defaults to "div".
 * @param props - The props for the column component, including:
 *   - `mainAxis`: Controls alignment along the main axis (vertical by default). One of "start" | "center" | "end" | "between" | "around" | "evenly". Defaults to "start".
 *   - `crossAxis`: Controls alignment along the cross axis (horizontal by default). One of "start" | "center" | "end" | "stretch" | "baseline". Defaults to "stretch".
 *   - `as`: The HTML element to render as. Defaults to "div".
 *   - `ref`: Optional ref for the rendered element.
 * @returns A React element representing the column.
 *
 * @example
 * ```tsx
 * <Column as="section" mainAxis="center" crossAxis="end" ref={myRef}>Content</Column>
 * ```
 */
const Column = forwardRef(ColumnComponent) as <T extends ColumnAs = "div">(
    props: ColumnProps<T> & { ref?: ForwardedRef<ColumnElementMap[T]> },
) => React.ReactElement;

// @ts-expect-error displayName is not typed on generic forwardRef
Column.displayName = "Column";

const ColumnSection = (props: ColumnProps<"section">) => {
    return <Column {...props} as="section" />;
};
const ColumnArticle = (props: ColumnProps<"article">) => {
    return <Column {...props} as="article" />;
};
const ColumnAside = (props: ColumnProps<"aside">) => {
    return <Column {...props} as="aside" />;
};
const ColumnMain = (props: ColumnProps<"main">) => {
    return <Column {...props} as="main" />;
};
const ColumnNav = (props: ColumnProps<"nav">) => {
    return <Column {...props} as="nav" />;
};
const ColumnHeader = (props: ColumnProps<"header">) => {
    return <Column {...props} as="header" />;
};
const ColumnFooter = (props: ColumnProps<"footer">) => {
    return <Column {...props} as="footer" />;
};
const ColumnDiv = (props: ColumnProps<"div">) => {
    return <Column {...props} as="div" />;
};

export {
    Column,
    ColumnArticle,
    ColumnAside,
    ColumnDiv,
    ColumnFooter,
    ColumnHeader,
    ColumnMain,
    ColumnNav,
    ColumnSection,
};
export type { ColumnAs, ColumnElementMap, ColumnProps };
